import { z } from "zod"

import { commandEnvelopeV1Schema } from "./command-envelope-v1.js"
import { safeEntitySummaryV1Schema } from "./command-result-v1.js"
import {
  commandIdSchema,
  correlationIdSchema,
  entityIdSchema,
} from "./identifiers.js"

const aggregateSchema = z
  .object({
    entityType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    entityId: entityIdSchema,
    expectedVersion: z.number().int().nonnegative().safe().nullable(),
  })
  .strict()

export const fieldOfflineCommandV1Schema = z
  .object({
    offlineCommandVersion: z.literal(1),
    queueSequence: z.number().int().positive().safe(),
    capturedAt: z.iso.datetime({ offset: true }),
    dependencies: z.array(commandIdSchema).max(10),
    aggregate: aggregateSchema,
    envelope: commandEnvelopeV1Schema,
  })
  .strict()
  .superRefine((command, context) => {
    const target = command.envelope.context
    if (
      target.entityType !== command.aggregate.entityType ||
      target.entityId !== command.aggregate.entityId ||
      command.envelope.expectedVersion !== command.aggregate.expectedVersion
    ) {
      context.addIssue({
        code: "custom",
        path: ["aggregate"],
        message: "Offline aggregate must match the command envelope.",
      })
    }
    if (command.dependencies.includes(command.envelope.commandId)) {
      context.addIssue({
        code: "custom",
        path: ["dependencies"],
        message: "Offline command cannot depend on itself.",
      })
    }
    if (new Set(command.dependencies).size !== command.dependencies.length) {
      context.addIssue({
        code: "custom",
        path: ["dependencies"],
        message: "Offline command dependencies must be unique.",
      })
    }
  })

export const fieldOfflineSyncRequestV1Schema = z
  .object({
    protocolVersion: z.literal(1),
    commands: z.array(fieldOfflineCommandV1Schema).min(1).max(25),
  })
  .strict()
  .superRefine((request, context) => {
    for (let index = 1; index < request.commands.length; index += 1) {
      if (
        request.commands[index]!.queueSequence <=
        request.commands[index - 1]!.queueSequence
      ) {
        context.addIssue({
          code: "custom",
          path: ["commands", index, "queueSequence"],
          message: "Offline commands must be in increasing queue order.",
        })
      }
    }
    const commandIds = request.commands.map(
      (command) => command.envelope.commandId,
    )
    if (new Set(commandIds).size !== commandIds.length) {
      context.addIssue({
        code: "custom",
        path: ["commands"],
        message: "Offline sync command IDs must be unique.",
      })
    }
    const positions = new Map(
      commandIds.map((commandId, index) => [commandId, index]),
    )
    request.commands.forEach((command, index) => {
      for (const dependency of command.dependencies) {
        const dependencyIndex = positions.get(dependency)
        if (dependencyIndex !== undefined && dependencyIndex >= index) {
          context.addIssue({
            code: "custom",
            path: ["commands", index, "dependencies"],
            message:
              "Dependencies present in a sync batch must precede their command.",
          })
        }
      }
    })
  })

export const fieldOfflineOutcomeV1Schema = z.enum([
  "applied",
  "conflict",
  "rejected",
  "retry",
  "blocked",
])

export const fieldOfflineSyncResultV1Schema = z
  .object({
    commandId: commandIdSchema,
    correlationId: correlationIdSchema,
    outcome: fieldOfflineOutcomeV1Schema,
    current: safeEntitySummaryV1Schema.nullable(),
    retryAfterSeconds: z.number().int().min(1).max(3600).nullable(),
  })
  .strict()
  .refine(
    (result) =>
      (result.outcome === "retry" || result.outcome === "blocked") ===
      (result.retryAfterSeconds !== null),
    {
      path: ["retryAfterSeconds"],
      message: "Only pending outcomes carry retry timing.",
    },
  )

export const fieldOfflineSyncResponseV1Schema = z
  .object({
    protocolVersion: z.literal(1),
    serverObservedAt: z.iso.datetime({ offset: true }),
    results: z.array(fieldOfflineSyncResultV1Schema).min(1).max(25),
  })
  .strict()

export type FieldOfflineCommandV1 = z.infer<
  typeof fieldOfflineCommandV1Schema
>
export type FieldOfflineSyncRequestV1 = z.infer<
  typeof fieldOfflineSyncRequestV1Schema
>
export type FieldOfflineOutcomeV1 = z.infer<
  typeof fieldOfflineOutcomeV1Schema
>
export type FieldOfflineSyncResultV1 = z.infer<
  typeof fieldOfflineSyncResultV1Schema
>
export type FieldOfflineSyncResponseV1 = z.infer<
  typeof fieldOfflineSyncResponseV1Schema
>
